import { Component, EventEmitter, Input, Output } from '@angular/core'
import { groupByKey } from '@engineering11/utility'
import { RequestedUpdateFields } from '../../../model/candidate-application.model'
import { UserMode } from '../../../model/component-modes.enum'
import { MatchType } from '../../../model/enum'
import { IMatchedSkill, ISkill } from '../../../model/interfaces'

const FilterTypes = Object.freeze({ ...MatchType, NoMatch: 'NoMatch' as const })
type FilterKeys = keyof typeof FilterTypes
const employerFilterTypeCopy: Record<FilterKeys, string> = {
  [FilterTypes.Exact]: 'Exact matches to the skills listed in the job post',
  [FilterTypes.Partial]: 'Partial matches to the skills listed in the job post',
  [FilterTypes.NoMatch]: 'Skills listed in the job post that the candidate does not have',
  [FilterTypes.NotListed]: 'Skills the candidate has that were not listed in the job post',
}

const candidateFilterTypeCopy: Record<FilterKeys, string> = {
  [FilterTypes.Exact]: employerFilterTypeCopy.Exact,
  [FilterTypes.Partial]: employerFilterTypeCopy.Partial,
  [FilterTypes.NoMatch]: 'Skills listed in the job post that you do not have',
  [FilterTypes.NotListed]: 'Skills you have that were not listed in the job post',
}

@Component({
  selector: 'skills-result',
  template: `<div class="skill-certifications">
      <!-- Tabs -->

      <div *ngIf="!pdfMode">
        <div *ngIf="matchedSkills.length !== 0; else emptyState">
          <e11-tabs
            tabTitleOverrides="e11-font-normal e11-text-base"
            [selectedTabId]="filterCurrent"
            (selectedTabChanged)="changedTab($event)"
            [tabsClassOverrides]="'e11-mb-2'"
          >
            <ng-container *ngFor="let filterType of filterTypes">
              <e11-tab
                *ngIf="!showOnlyMissing || (showOnlyMissing && filterType === FilterTypes.NoMatch)"
                [title]="translationFilterTypes[filterType] | translate"
                [tabId]="filterType"
              >
                <ng-container *ngIf="filterCurrent === filterType">
                  <p *ngIf="userMode === UserMode.Employer">
                    {{ employerFilterTypeCopy[filterType] | translate }}
                  </p>
                  <p *ngIf="userMode === UserMode.Candidate">
                    {{ candidateFilterTypeCopy[filterType] | translate }}
                  </p>
                  <ng-container *ngIf="groupedSkills[filterCurrent] && groupedSkills[filterCurrent].length > 0; else noSkill">
                    <manage-skills
                      [readOnly]="readOnly || !(userMode === UserMode.Candidate && filterCurrent === FilterTypes.NoMatch)"
                      [markAsMissingSkills]="readOnly || (userMode === UserMode.Candidate && filterCurrent === FilterTypes.NoMatch)"
                      [currentSkills]="groupedSkills[filterCurrent]"
                      [loading]="false"
                      (newSkills)="newSkills.emit($event)"
                    ></manage-skills>
                  </ng-container>
                  <ng-template #noSkill>
                    <h4 class="e11-font-bold e11-text-center e11-my-8">{{ 'No matching skills' | translate }}</h4>
                  </ng-template>
                </ng-container>
              </e11-tab>
            </ng-container>
          </e11-tabs>
        </div>
      </div>

      <!-- EXPANDED FOR PDF -->
      <div *ngIf="pdfMode">
        <ng-container *ngFor="let filterType of filterTypes">
          <ng-container *ngIf="groupedSkills[filterType].length > 0">
            <section class="e11-mb-8" *ngIf="!showOnlyMissing || (showOnlyMissing && filterType === FilterTypes.NoMatch)">
              <p class="e11-mb-1 e11-text-md e11-text-skin-base e11-font-bold e11-linebreak ng-star-inserted">
                <span class="e11-border-b e11-border-skin-grey/50">{{ translationFilterTypes[filterType] | translate }}</span>
              </p>
              <p>
                {{ employerFilterTypeCopy[filterType] | translate }}
              </p>
              <ng-container *ngIf="groupedSkills[filterType] && groupedSkills[filterType].length > 0; else noSkill">
                <manage-skills
                  [readOnly]="readOnly || !(userMode === UserMode.Candidate && filterType === FilterTypes.NoMatch)"
                  [markAsMissingSkills]="readOnly || (userMode === UserMode.Candidate && filterType === FilterTypes.NoMatch)"
                  [currentSkills]="groupedSkills[filterType]"
                  [loading]="false"
                  (newSkills)="newSkills.emit($event)"
                ></manage-skills>
              </ng-container>
              <ng-template #noSkill>
                <h4 class="e11-font-bold e11-text-center e11-my-8">{{ 'No matching skills' | translate }}</h4>
              </ng-template>
            </section>
          </ng-container>
        </ng-container>
      </div>
    </div>

    <ng-template #emptyState>
      <e11-empty-state
        *ngIf="!skills && userMode === UserMode.Candidate"
        [icon]="'construction'"
        [iconSize]="'md-36'"
        title="{{ 'No Skills' | translate }}"
        [clickable]="false"
        subtitle="{{ 'You have not added any skills.' | translate }}"
      >
      </e11-empty-state>
      <e11-empty-state
        *ngIf="!skills && userMode !== UserMode.Candidate"
        [icon]="'construction'"
        [iconSize]="'md-36'"
        title="{{ 'No Skills' | translate }}"
        [subtitle]="
          openRequest ? 'Requested on ' + (requestTimestamp | date : 'MM/dd/yyyy - hh:mm a') : ('The candidate did not add skills.' | translate)
        "
        [clickable]="false"
        [borderColor]="openRequest ? 'e11-border-skin-secondary' : ''"
        [showButton]="true"
        [buttonValue]="openRequest ? 'Pending Response' : 'Request Skills'"
        [buttonColor]="
          openRequest
            ? 'e11-border-skin-secondary e11-text-skin-secondary'
            : 'e11-border-skin-primary-accent hover:e11-bg-skin-primary-accent/20 e11-text-skin-primary-accent'
        "
        [buttonIcon]="openRequest ? 'pending' : ''"
        (buttonClicked)="!requestsDisabled && requestModal()"
        [disabled]="!!openRequest"
      >
      </e11-empty-state>
    </ng-template> `,
  styleUrls: ['./skills.component.scss'],
})
export class CandidateSkillsResultComponent {
  FilterTypes = FilterTypes
  employerFilterTypeCopy = employerFilterTypeCopy
  candidateFilterTypeCopy = candidateFilterTypeCopy
  UserMode = UserMode
  filterTypes = Object.values(FilterTypes)
  filterCurrent: FilterKeys = MatchType.Exact

  translationFilterTypes = {
    Exact: 'Exact',
    Partial: 'Partial',
    NotListed: 'Other',
    NoMatch: 'Missing',
  }

  groupedSkills: Record<string, ISkill[]> = Object.values(FilterTypes).reduce((prv, nxt) => ({ ...prv, [nxt]: [] }), {})

  // Needed for empty state
  matchedSkills!: IMatchedSkill[]

  showOnlyMissing = false

  constructor() {}

  @Output() newSkills = new EventEmitter<ISkill[]>()
  @Output() requestFromComponent = new EventEmitter()

  @Input() openRequest?: RequestedUpdateFields
  @Input() requestsDisabled = false
  @Input() pdfMode: boolean = false
  @Input() showRequestButton = true
  @Input() requestTimestamp?: string

  @Input()
  userMode: UserMode = UserMode.Candidate

  @Input() set skills(matchedSkills: IMatchedSkill[]) {
    this.matchedSkills = matchedSkills
    this.groupedSkills = { ...this.groupedSkills, ...groupByKey(matchedSkills, 'matchType') }
  }

  @Input() set missingSkills(missingSkills: ISkill[]) {
    this.groupedSkills = { ...this.groupedSkills, NoMatch: missingSkills }
  }

  @Input() set showOnlyMissingSkills(showOnlyMissing: boolean) {
    this.showOnlyMissing = showOnlyMissing

    if (this.showOnlyMissing) this.filterCurrent = this.FilterTypes.NoMatch
  }

  @Input() readOnly = false

  requestModal() {
    this.requestFromComponent.emit('Skills')
  }

  changedTab(event: any) {
    this.filterCurrent = event.tabId as FilterKeys
  }
}
